import React, { useEffect, useMemo, useRef, useState } from 'react';
import { allCountries } from 'country-region-data';
import { useTranslation } from 'react-i18next';

import { OpSelectOptionType } from 'new-components/DLS/OpSelect/OpSelect';
import {
  OpFormSelect,
  OpFormSelectProps,
} from 'new-components/DLS/OpForm/formComponents/OpFormSelect/OpFormSelect';

export const OpFormStateSelect = ({
  selectedCountry,
  value,
  onChange,
  ...props
}: OpFormSelectProps<string, any> & {
  selectedCountry: string;
  value: string | null | undefined;
  onChange: (value: string | string[] | null) => void;
}) => {
  const { t } = useTranslation();
  const [currentCountry, setCurrentCountry] = useState(selectedCountry);
  const previousCountryRef = useRef(selectedCountry);
  const [countryRegionOptionsMap, countryRegionShortCodesMap] = useMemo(() => {
    const _countryRegionOptionsMap = new Map();
    const _countryRegionShortCodesMap = new Map();
    for (const country of allCountries) {
      const [, countryShortCode, countryRegions] = country;
      const regionOptions = [] as OpSelectOptionType[];
      const regionShortCodes = [] as string[];

      countryRegions.forEach(([name, shortCode]) => {
        regionOptions.push({
          key: shortCode,
          value: shortCode,
          label: name,
        });
        regionShortCodes.push(shortCode);
      });

      _countryRegionOptionsMap.set(
        countryShortCode, // short code
        regionOptions, // regions
      );
      _countryRegionShortCodesMap.set(
        countryShortCode, // short code
        regionShortCodes, // regions short codes
      );
    }
    return [_countryRegionOptionsMap, _countryRegionShortCodesMap];
  }, []);

  const isCountryValid =
    currentCountry && countryRegionShortCodesMap.get(currentCountry);

  useEffect(() => {
    if (
      previousCountryRef.current &&
      previousCountryRef.current !== selectedCountry
    ) {
      const isValidState = countryRegionShortCodesMap
        .get(selectedCountry)
        ?.includes(value);

      if (onChange && !isValidState) {
        onChange(null);
      }
    }
    previousCountryRef.current = selectedCountry;
    setCurrentCountry(selectedCountry);
  }, [onChange, selectedCountry, countryRegionShortCodesMap, value]);

  return (
    <OpFormSelect
      {...props}
      mode={undefined}
      disabled={!isCountryValid || props.disabled}
      onChange={onChange}
      value={value}
      // if you're having problems with the value not being updated, be aware of https://github.com/ant-design/ant-design/issues/41889
      options={countryRegionOptionsMap.get(currentCountry) || []}
      tooltip={
        !isCountryValid && value ? t('Select a country first') : undefined
      }
      placeholder={
        !isCountryValid && !value
          ? t('Select a country first')
          : t('Select a state')
      }
    />
  );
};
